import { mainApiUrl, baseHeaders, localApiUrl, storeId } from '@constants'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { getStateFromLocalStorage, storeStateAtLocalStorage } from '@src/reduxjs'
import { getCookieConsentValue } from "react-cookie-consent"
import { getTerminalId, base62Encode } from '@src/utils'
import ms from 'ms'

const initialState = {
  isSidebarOpen: false,
  isPricingOpen: false,
  isCartDrawerOpen: false,
  bottomMessage: {
    text: '',
    icon: '',
  },
  progressBar: {
    visible: false,
    value: 0,
  },
  lastVisitedLocation: '',
  isStore: false,
  localId: '',
  cookiesAccepted: getCookieConsentValue("CookiesAccepted"),
  scrollPosition: 0,
  isSidebarLocked: true,
  terminalId: '',
  folders: [],
}

export const mercadoPagoCreditTopUpThunk = createAsyncThunk('main/mercadoPagoCreditTopUpThunk', async ({ count }, { getState, rejectWithValue }) => {
  const headers = { ...baseHeaders, Authorization: `JWT ${getState().auth.jwtToken}` }
  const response = await fetch(`${mainApiUrl}/stores/${storeId}/credits/add?count=${count}`, { signal: AbortSignal.timeout(ms('20s')), headers, method: 'GET' })
  const json = await response.json()

  const { status } = response

  if (status !== 201) {
    return rejectWithValue({ status })
  }

  return json
})

export const deletePhotoThunk = createAsyncThunk('main/deletePhotoThunk', async (photo, { getState, rejectWithValue }) => {
  const headers = { ...baseHeaders, Authorization: `JWT ${getState().auth.jwtToken}` }
  const response = await fetch(`${mainApiUrl}/photos/${photo.id}`, { signal: AbortSignal.timeout(ms('10s')), headers, method: 'DELETE' })
  const { status } = response

  if (status !== 204) {
    return rejectWithValue({ status })
  }

  return { status }
})

export const pingLocalStoreThunk = createAsyncThunk('main/pingLocalStoreThunk', async ({ timeout } = {}, { getState, rejectWithValue }) => {
  const response = await fetch(`${localApiUrl}/ping`, { signal: AbortSignal.timeout(timeout || ms('10s')), headers: baseHeaders, method: 'GET' })
  const json = await response.json()

  const { status } = response

  if (status !== 200) {
    return rejectWithValue({ status })
  }

  return json
})

const mainSlice = createSlice({
  name: 'main',
  initialState: { ...initialState, ...getStateFromLocalStorage('mainSlice') },
  reducers: {
    closeSidebar: (state) => {
      state.isSidebarOpen = false

      storeStateAtLocalStorage('mainSlice', { isSidebarOpen: state.isSidebarOpen })
    },
    openSidebar: (state) => {
      state.isSidebarOpen = true

      storeStateAtLocalStorage('mainSlice', { isSidebarOpen: state.isSidebarOpen })
    },
    toggleSidebar: (state) => {
      state.isSidebarOpen = !state.isSidebarOpen

      storeStateAtLocalStorage('mainSlice', { isSidebarOpen: state.isSidebarOpen })
    },
    toggleSidebarLocked: (state, action) => {
      state.isSidebarLocked = !state.isSidebarLocked

      if (!state.isSidebarLocked) {
        state.isSidebarOpen = false
      }

      storeStateAtLocalStorage('mainSlice', { isSidebarLocked: state.isSidebarLocked, isSidebarOpen: state.isSidebarOpen })
    },
    closePricing: (state) => {
      state.isPricingOpen = false
    },
    openPricing: (state) => {
      state.isPricingOpen = true
    },
    togglePricing: (state) => {
      state.isPricingOpen = !state.isPricingOpen
    },
    closeCartDrawer: (state) => {
      state.isCartDrawerOpen = false
    },
    openCartDrawer: (state) => {
      state.isCartDrawerOpen = true
    },
    toggleCartDrawer: (state) => {
      state.isCartDrawerOpen = !state.isCartDrawerOpen
    },
    setScrollPosition: (state, action) => {
      state.scrollPosition = action.payload || initialState.scrollPosition
    },
    setCookiesAccepted: (state, action) => {
      state.cookiesAccepted = [true, false].includes(action.payload) ? action.payload : initialState.cookiesAccepted
    },
    setLastVisitedLocation: (state, action) => {
      state.lastVisitedLocation = action.payload || initialState.lastVisitedLocation
    },
    setBottomMessage: (state, action) => {
      state.bottomMessage = action.payload || initialState.bottomMessage
    },
    setProgressBarValue: (state, action) => {
      state.progressBar.value = action.payload || initialState.progressBar.value
    },
    setProgressBarVisibility: (state, action) => {
      state.progressBar.visible = action.payload || initialState.progressBar.visible
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(({ type }) => type === `${pingLocalStoreThunk.rejected}`, (state, action) => {
      state.isStore = false
      state.localId = initialState.localId
      state.folders = initialState.folders
    }).addMatcher(({ type }) => type === `${pingLocalStoreThunk.fulfilled}`, (state, action) => {
      const { payload: { data: { id, store, req_ip: reqIp, folders } } } = action

      state.isStore = Boolean(store && store.id && store.id === storeId)
      state.localId = id
      state.terminalId = base62Encode(reqIp || '') || getTerminalId()
      state.folders = folders || initialState.folders
    })
  }
})

export const {
  closeSidebar,
  openSidebar,
  closePricing,
  openPricing,
  togglePricing,
  closeCartDrawer,
  openCartDrawer,
  toggleCartDrawer,
  setBottomMessage,
  setLastVisitedLocation,
  setProgressBarValue,
  setProgressBarVisibility,
  toggleSidebar,
  setCookiesAccepted,
  setScrollPosition,
  toggleSidebarLocked,
} = mainSlice.actions

export default mainSlice
